import React, { useState } from 'react'
import { SortingArrows } from 'shared'
import { ActiveSort } from 'shared/components/SortingArrows'
import SmoothCollapse from 'react-smooth-collapse'
import './Table.scss'

export interface Row<T> {
	original: T
	isExpanded: boolean
}
export interface ITableHeader<T> {
	accessor: keyof T
	header: string
	show?: boolean
	align?: 'left' | 'center' | 'right'
	Cell?: (Row: Row<T>, key: number) => any
	Sort?: (sort: 'asc' | 'desc') => any
}
export interface ITable<T> {
	columns: ITableHeader<T>[]
	data: T[]
	subData?: (rowData: T) => JSX.Element
}

function Table<T>({ columns, data, subData }: ITable<T>) {
	const shownColumns = columns.filter(
		(x) => x.show === undefined || x.show === true
	)

	const [expandedRows, setExpandedRows] = useState<string[]>([])
	const [activeSort, setActiveSort] = useState<string>()
	const [activeSortArrow, setActiveSortArrow] = useState<ActiveSort>(
		ActiveSort.None
	)

	const isRowExpanded = (id: string) => expandedRows.includes(id)
	const getDataAlignment = (align?: 'left' | 'center' | 'right') => {
		switch (align) {
			case 'center':
				return 'align-center'
			case 'right':
				return 'align-right'
			case 'left':
			default:
				return 'align-left'
		}
	}

	const headerWithSort = (column: ITableHeader<T>) => {
		return (
			<button
				onClick={() => {
					let newSortArrow
					if (column.header === activeSort) {
						newSortArrow =
							activeSortArrow === ActiveSort.Asc
								? ActiveSort.Desc
								: ActiveSort.Asc
					} else {
						newSortArrow = ActiveSort.Asc
					}

					setActiveSortArrow(newSortArrow)
					setActiveSort(column.header)

					if (column.Sort) {
						column.Sort(
							newSortArrow === ActiveSort.Asc ? 'asc' : 'desc'
						)
					}
				}}>
				<div>{column.header}</div>
				<SortingArrows
					sortAsc={() => {}}
					sortDesc={() => {}}
					useActiveArrow={
						activeSort === column.header
							? activeSortArrow
							: ActiveSort.None
					}
				/>
			</button>
		)
	}

	return (
		<table className="table">
			<thead>
				<tr>
					{shownColumns.map((header, key) => {
						return (
							<React.Fragment key={key}>
								<th>
									<div
										className={`table-header ${getDataAlignment(
											header.align
										)}`}>
										{header.Sort
											? headerWithSort(header)
											: header.header}
									</div>
								</th>
							</React.Fragment>
						)
					})}
				</tr>
			</thead>
			<tbody>
				{data?.map((d, key) => {
					return (
						<React.Fragment key={key}>
							<tr
								role="button"
								className={`main-row ${
									subData ? 'expandable' : ''
								}`}
								onClick={() => {
									const id = key.toString()
									if (isRowExpanded(id)) {
										const temp = expandedRows.filter(
											(x) => x !== id
										)
										setExpandedRows(temp)
									} else {
										setExpandedRows([...expandedRows, id])
									}
								}}>
								{shownColumns.map((header, key2) => {
									const shownValue = header.Cell
										? header.Cell(
												{
													original: d,
													isExpanded: isRowExpanded(
														key.toString()
													),
												},
												key
										  )
										: d[header.accessor]
									return (
										<td
											key={`${header.accessor.toString()}-${key}-${key2}`}
											className={`${getDataAlignment(
												header.align
											)}`}>
											{shownValue}
										</td>
									)
								})}
							</tr>
							{subData && (
								<tr className="sub-row">
									<td colSpan={shownColumns.length}>
										<SmoothCollapse
											expanded={isRowExpanded(
												key.toString()
											)}
											children={subData(d)}
										/>
									</td>
								</tr>
							)}
						</React.Fragment>
					)
				})}
			</tbody>
		</table>
	)
}

export default Table